import { useState, useEffect, useRef } from 'react';
import { supabase, supabaseHealthIntent } from '../../supabaseClient.js';
import { useSelector, shallowEqual } from 'react-redux';

// Kept for backward-compat with components that still destructure it (e.g. ChatScreen's
// `phoneNumber` prop). The new inbox is keyed by wa_messages.chat_room_id, not this number.
const TEAM_PHONE_NUMBER = process.env.REACT_APP_TEAM_PHONE_NUMBER || '85262763482';

// wa_messages is plaintext (no encryption). We expose a few aliases so the existing UI
// components keep working with their old field names while we read the new schema:
//   ispinned       <- is_pinned
//   isuser         <- sender_type === 'therapist' (i.e. a message from "our" side)
//   replymessageid <- reply_to_message_id
const normalize = (m) => ({
  ...m,
  ispinned: m.is_pinned,
  isuser: m.sender_type === 'therapist',
  replymessageid: m.reply_to_message_id,
});

export const useChatsData = () => {
  const [contactList, setContactList] = useState([]);
  const [textMessages, setTextMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const { userDetails } = useSelector((state) => ({userDetails: state.globalState.userDetails}), shallowEqual);
  const [subscriptionActiveStatus, setSubscriptionActiveStatus] = useState(null);
  const currentOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);
  const hasMounted = useRef(false);

  useEffect(() => {
    if (hasMounted.current) { return; }
    fetchSubscriptionInfo();
    fetchTextMessages();
    hasMounted.current = true;
  }, []);

  // Realtime: live updates from the health-intent project's wa_messages table.
  // No per-field decryption needed (plaintext), so the handler is straightforward.
  useEffect(() => {
    const channel = supabaseHealthIntent
      .channel('wa-messages-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'wa_messages',
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const row = payload.new;
            if (row.is_deleted) return;
            const newMessage = normalize(row);
            setTextMessages((current) =>
              current.some((m) => m.id === newMessage.id) ? current : [...current, newMessage]
            );
            setContactList((current) =>
              current.includes(newMessage.chat_room_id) ? current : [...current, newMessage.chat_room_id]
            );
          } else if (payload.eventType === 'UPDATE') {
            const row = payload.new;
            if (row.is_deleted) {
              // Soft-deleted -> remove from the inbox
              setTextMessages((current) => current.filter((m) => m.id !== row.id));
              return;
            }
            const updatedMessage = normalize(row);
            setTextMessages((current) => {
              const exists = current.some((m) => m.id === updatedMessage.id);
              return exists
                ? current.map((m) => (m.id === updatedMessage.id ? updatedMessage : m))
                : [...current, updatedMessage];
            });
          } else if (payload.eventType === 'DELETE') {
            setTextMessages((current) => current.filter((m) => m.id !== payload.old.id));
          }
        }
      )
      .subscribe();
    return () => {
      supabaseHealthIntent.removeChannel(channel);
    };
  }, []);

  // Subscription status still comes from the MAIN project (the therapist's account lives there).
  const fetchSubscriptionInfo = async () => {
    try {
      if (!userDetails || !userDetails.id) return;

      const { data, error } = await supabase.from('subscription_info').select('is_active').eq('user_id', userDetails.id).single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching subscription info:', error);
        return;
      }
      if(!data) {
        setSubscriptionActiveStatus(false);
        return;
      }
      if (data) {
        setSubscriptionActiveStatus(data.is_active);
      }
    } catch (error) {
      console.error('Error in fetchSubscriptionInfo:', error);
    }
  };

  const fetchTextMessages = async () => {
    setMessagesLoading(true);
    const { data, error } = await supabaseHealthIntent
      .from('wa_messages')
      .select('*')
      .eq('is_deleted', false)
      .order('created_at', { ascending: false });
    if (error) {
      console.error(error);
    } else {
      const normalized = data.map(normalize);
      setTextMessages(normalized);
      const uniqueRooms = [...new Set(normalized.map((m) => m.chat_room_id).filter((room) => room != null))];
      setContactList(uniqueRooms);
    }
    setMessagesLoading(false);
  }

  // Therapist reply. Stored in wa_messages with sender_type='therapist' so it shows up in this
  // inbox, but it is NOT delivered to the patient's WhatsApp (we don't call any send endpoint).
  const insertTextMessage = async (chat_room_id, message, reply_to_message_id) => {
    let { error } = await supabaseHealthIntent.from('wa_messages').insert([{
      chat_room_id,
      message,
      sender_type: 'therapist',
      sender_name: userDetails?.name ?? null,
      is_pinned: false,
      reply_to_message_id: reply_to_message_id ?? null,
    }]);
    if (error){
      console.error(error);
    }
  }

  const updateTextMessage = async (id, ispinned) => {
    let { error } = await supabaseHealthIntent.from('wa_messages').update({ is_pinned: ispinned }).eq('id', id);
    if (error){
      console.error(error);
    }
  }

  // Soft-delete: hide from the inbox without destroying the underlying patient/AI record.
  const deleteTextMessage = async (id) => {
    let { error } = await supabaseHealthIntent.from('wa_messages').update({ is_deleted: true }).eq('id', id);
    if (error){
      console.error(error);
    }
    setTextMessages((current) => current.filter((m) => m.id !== id));
  }

  return {
    contactList, userDetails, textMessages, insertTextMessage, updateTextMessage, deleteTextMessage, TEAM_PHONE_NUMBER, subscriptionActiveStatus, currentOrg, messagesLoading
  };
};
